import GppGoodRounded from '@mui/icons-material/GppGoodRounded';
import GppMaybeRounded from '@mui/icons-material/GppMaybeRounded';
import KeyboardDoubleArrowLeftRounded from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import MenuRounded from '@mui/icons-material/MenuRounded';
import PersonOutlineRounded from '@mui/icons-material/PersonOutlineRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import {
  alpha,
  Avatar,
  Badge,
  Box,
  ButtonBase,
  Chip,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth } from '../auth/AuthProvider';
import { ROLE_LABELS } from '../auth/capabilities';
import { custom, fonts } from '../design/theme';
import { hasScreen } from '../features';
import { useSupportUnread } from '../features/support-chat/api';
import { initials } from '../lib/format';
import { CommandPalette } from './CommandPalette';
import { visibleNav } from './nav';
import { BrandMark, ModeToggle } from './parts';
import { PoweredBy } from '../components/PoweredBy';

const SIDEBAR = 264;
const SIDEBAR_COLLAPSED = 76;
const COLLAPSE_KEY = 'sm.admin.sidebar';
const sb = custom.sidebar;

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
}

export function AppShell() {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();
  // The mobile drawer belongs to the page it was opened on, so navigating closes it.
  const [mobileOpenOn, setMobileOpenOn] = useState<string | null>(null);
  const mobileOpen = mobileOpenOn === location.pathname;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const toggle = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1');
      } catch {
        /* ignore */
      }
      return !c;
    });

  const width = desktop && collapsed ? SIDEBAR_COLLAPSED : SIDEBAR;
  const sidebar = (
    <Sidebar collapsed={desktop && collapsed} onToggle={toggle} showToggle={desktop} />
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.default' }}>
      {desktop ? (
        <Box
          component="nav"
          aria-label="Main"
          sx={{
            width,
            flexShrink: 0,
            transition: theme.transitions.create('width', { duration: 220 }),
            position: 'sticky',
            top: 0,
            height: '100dvh',
          }}
        >
          {sidebar}
        </Box>
      ) : (
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpenOn(null)}
          slotProps={{ paper: { sx: { width: SIDEBAR, border: 0, bgcolor: 'transparent' } } }}
        >
          <Box component="nav" aria-label="Main" sx={{ height: '100%' }}>
            {sidebar}
          </Box>
        </Drawer>
      )}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <TopBar
          onMenu={() => setMobileOpenOn(location.pathname)}
          showMenu={!desktop}
          onSearch={() => setPaletteOpen(true)}
        />
        <Box
          component="main"
          key={location.pathname}
          sx={{
            flex: 1,
            px: { xs: 2, sm: 3, lg: 4 },
            py: { xs: 2.5, sm: 3.5 },
            maxWidth: 1480,
            width: '100%',
            mx: 'auto',
            animation: 'sm-page-in 220ms cubic-bezier(0.2,0,0,1)',
            '@keyframes sm-page-in': {
              from: { opacity: 0, transform: 'translateY(4px)' },
              to: { opacity: 1, transform: 'none' },
            },
          }}
        >
          <Outlet />
        </Box>
      </Box>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </Box>
  );
}

function Sidebar({
  collapsed,
  onToggle,
  showToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
  showToggle: boolean;
}) {
  const { can, me } = useAuth();
  const groups = visibleNav((k) => can(k));
  const waiting = useSupportUnread().data?.conversations ?? 0;

  return (
    <Box
      sx={(t) => ({
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: sb.bg,
        color: sb.text,
        borderRight: `1px solid ${sb.border}`,
        position: 'relative',
        overflow: 'hidden',
        // A faint green glow behind the brand.
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -120,
          left: -80,
          width: 320,
          height: 320,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(52,168,135,0.30), transparent 65%)',
          pointerEvents: 'none',
        },
        ...t.applyStyles('dark', { background: sb.bgDark }),
      })}
    >
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          alignItems: 'center',
          px: collapsed ? 2.25 : 2.5,
          height: 68,
          flexShrink: 0,
          position: 'relative',
        }}
      >
        <BrandMark />
        {!collapsed && (
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle1"
              noWrap
              sx={{
                fontFamily: fonts.display,
                fontWeight: 700,
                fontSize: '0.875rem',
                letterSpacing: '-0.01em',
                lineHeight: 1.2,
                color: sb.textStrong,
              }}
            >
              PackPilot
            </Typography>
            <Typography
              variant="caption"
              noWrap
              component="div"
              sx={{ color: sb.accent, fontWeight: 600, letterSpacing: '0.02em' }}
            >
              Platform console
            </Typography>
          </Box>
        )}
        {showToggle && !collapsed && (
          <Tooltip title="Collapse sidebar">
            <IconButton
              size="small"
              onClick={onToggle}
              aria-label="Collapse sidebar"
              sx={{ color: sb.muted, '&:hover': { color: sb.textStrong, bgcolor: sb.hover } }}
            >
              <KeyboardDoubleArrowLeftRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1.5,
          pb: 2,
          scrollbarWidth: 'thin',
          position: 'relative',
        }}
      >
        {groups.map((group) => (
          <Box key={group.title} sx={{ mt: 1.75 }}>
            {!collapsed && (
              <Typography
                variant="overline"
                component="div"
                sx={{ px: 1.5, mb: 0.5, color: sb.muted }}
              >
                {group.title}
              </Typography>
            )}
            {group.items.map((item) => (
              <NavButton
                key={item.path}
                {...item}
                collapsed={collapsed}
                badge={item.path === '/inbox' ? waiting : 0}
              />
            ))}
          </Box>
        ))}
      </Box>

      {me && <SecurityCard collapsed={collapsed} mfaEnabled={me.mfaEnabled} />}
      {!collapsed && <PoweredBy onDark sx={{ pb: 1.5, px: 1.5 }} />}

      {showToggle && collapsed && (
        <Box sx={{ p: 1.5, pt: 0, display: 'flex', justifyContent: 'center' }}>
          <Tooltip title="Expand sidebar" placement="right">
            <IconButton
              size="small"
              onClick={onToggle}
              aria-label="Expand sidebar"
              sx={{ color: sb.muted, transform: 'rotate(180deg)' }}
            >
              <KeyboardDoubleArrowLeftRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}

/** Two-step verification status at the foot of the sidebar; links to the account page. */
function SecurityCard({ collapsed, mfaEnabled }: { collapsed: boolean; mfaEnabled: boolean }) {
  const Icon = mfaEnabled ? GppGoodRounded : GppMaybeRounded;
  const color = mfaEnabled ? '#6EE7B7' : '#FCD34D';
  const card = (
    <ButtonBase
      component={NavLink}
      to="/account"
      aria-label={mfaEnabled ? 'Two-step verification on' : 'Two-step verification off — set it up'}
      sx={{
        m: 1.5,
        p: collapsed ? 1 : 1.5,
        gap: 1.25,
        justifyContent: collapsed ? 'center' : 'flex-start',
        borderRadius: '12px',
        border: `1px solid ${sb.border}`,
        bgcolor: 'rgba(255,255,255,0.04)',
        textAlign: 'left',
        '&:hover': { bgcolor: sb.hover },
      }}
    >
      <Icon sx={{ color, fontSize: 22 }} />
      {!collapsed && (
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            component="div"
            sx={{ color: sb.textStrong, fontWeight: 650 }}
          >
            {mfaEnabled ? 'Two-step verification on' : 'Secure your account'}
          </Typography>
          <Typography variant="caption" component="div" sx={{ color: sb.muted, lineHeight: 1.3 }}>
            {mfaEnabled ? 'Your sign-ins need a code' : 'Turn on two-step verification'}
          </Typography>
        </Box>
      )}
    </ButtonBase>
  );
  return collapsed ? (
    <Tooltip
      title={mfaEnabled ? 'Two-step verification on' : 'Secure your account'}
      placement="right"
    >
      {card}
    </Tooltip>
  ) : (
    card
  );
}

function NavButton({
  label,
  path,
  icon: Icon,
  collapsed,
  badge = 0,
}: {
  label: string;
  path: string;
  icon: typeof MenuRounded;
  collapsed: boolean;
  /** A count shown beside the label (a dot on the icon while collapsed). */
  badge?: number;
}) {
  const soon = !hasScreen(path);
  const button = (
    <ButtonBase
      component={NavLink}
      to={path}
      end={path === '/'}
      sx={{
        width: '100%',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: 1.5,
        px: collapsed ? 0 : 1.5,
        height: 40,
        mb: 0.25,
        borderRadius: '10px',
        color: sb.text,
        fontSize: '0.875rem',
        fontWeight: 550,
        position: 'relative',
        transition: 'background-color 140ms, color 140ms',
        '&:hover': { bgcolor: sb.hover, color: sb.textStrong },
        '&.Mui-focusVisible': { outline: `2px solid ${sb.accent}`, outlineOffset: 1 },
        '&.active': {
          color: sb.textStrong,
          bgcolor: sb.active,
          fontWeight: 650,
          boxShadow: 'inset 0 0 0 1px rgba(165,180,252,0.14)',
          '& .nav-icon': { color: sb.accent },
          '&::before': {
            content: '""',
            position: 'absolute',
            left: -12,
            top: 10,
            bottom: 10,
            width: 3,
            borderRadius: 3,
            bgcolor: sb.accent,
          },
        },
      }}
    >
      <Badge color="error" variant="dot" invisible={!collapsed || badge === 0}>
        <Icon className="nav-icon" sx={{ fontSize: 20 }} />
      </Badge>
      {!collapsed && (
        <>
          <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>
            {label}
          </Box>
          {badge > 0 && (
            <Box
              component="span"
              aria-label={`${String(badge)} waiting`}
              sx={{
                minWidth: 20,
                height: 20,
                px: 0.75,
                borderRadius: '10px',
                bgcolor: 'error.main',
                color: 'error.contrastText',
                fontSize: 11,
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {badge > 99 ? '99+' : badge}
            </Box>
          )}
          {soon && (
            <Box
              component="span"
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: sb.muted,
                letterSpacing: '0.06em',
                px: 0.75,
                py: 0.125,
                borderRadius: '6px',
                border: `1px solid ${sb.border}`,
              }}
            >
              SOON
            </Box>
          )}
        </>
      )}
    </ButtonBase>
  );
  return collapsed ? (
    <Tooltip
      title={soon ? `${label} (soon)` : badge > 0 ? `${label} (${String(badge)})` : label}
      placement="right"
    >
      {button}
    </Tooltip>
  ) : (
    button
  );
}

function TopBar({
  onMenu,
  showMenu,
  onSearch,
}: {
  onMenu: () => void;
  showMenu: boolean;
  onSearch: () => void;
}) {
  const { me } = useAuth();
  const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.userAgent);

  return (
    <Box
      component="header"
      sx={(t) => ({
        position: 'sticky',
        top: 0,
        zIndex: t.zIndex.appBar,
        height: 68,
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.5, sm: 1.5 },
        px: { xs: 1.5, sm: 3, lg: 4 },
        backdropFilter: 'saturate(180%) blur(14px)',
        backgroundColor: alpha(t.palette.background.default, 0.8),
        ...t.applyStyles('dark', { backgroundColor: alpha('#0B0D1A', 0.75) }),
        borderBottom: `1px solid ${t.vars?.palette.divider ?? t.palette.divider}`,
      })}
    >
      {showMenu && (
        <IconButton onClick={onMenu} edge="start" aria-label="Open navigation">
          <MenuRounded />
        </IconButton>
      )}
      <ButtonBase
        onClick={onSearch}
        aria-label="Search or jump to a screen"
        sx={(t) => ({
          flex: 1,
          minWidth: 0,
          maxWidth: 440,
          height: 40,
          px: 1.5,
          gap: 1,
          justifyContent: 'flex-start',
          borderRadius: '10px',
          color: 'text.secondary',
          bgcolor: 'background.paper',
          border: `1px solid ${t.vars?.palette.divider ?? t.palette.divider}`,
          boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
          fontSize: '0.875rem',
          transition: 'border-color 140ms',
          '&:hover': { borderColor: 'grey.300' },
        })}
      >
        <SearchRounded sx={{ fontSize: 20 }} />
        <Box
          component="span"
          sx={{
            flex: 1,
            textAlign: 'left',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          Search or jump to…
        </Box>
        <Box
          component="kbd"
          sx={{
            display: { xs: 'none', sm: 'inline-flex' },
            fontFamily: 'inherit',
            fontSize: 11,
            fontWeight: 600,
            px: 0.75,
            py: 0.25,
            borderRadius: '6px',
            border: 1,
            borderColor: 'divider',
            color: 'text.secondary',
          }}
        >
          {isMac ? '⌘K' : 'Ctrl K'}
        </Box>
      </ButtonBase>
      <Box sx={{ flex: { xs: 0, sm: 1 } }} />
      <ModeToggle />
      {me && <AccountMenu />}
    </Box>
  );
}

/** Avatar button with the admin's name and role; menu: account, security, sign out. */
export function AccountMenu() {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!me) return null;
  const role = ROLE_LABELS[me.role];
  const go = (path: string) => {
    setAnchor(null);
    void navigate(path);
  };
  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={`Account menu: ${me.name}, ${role}`}
        aria-haspopup="menu"
        sx={{ borderRadius: '12px', p: 0.5, pr: { xs: 0.5, md: 1.25 }, gap: 1.25, flexShrink: 0 }}
      >
        <Avatar
          sx={{
            width: 34,
            height: 34,
            fontSize: 13,
            color: '#fff',
            background: 'linear-gradient(135deg, #0F8A73, #05473E)',
          }}
        >
          {initials(me.name)}
        </Avatar>
        <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'left' }}>
          <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
            {me.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {role}
          </Typography>
        </Box>
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 260 } } }}
      >
        <Stack direction="row" spacing={1.5} sx={{ px: 1.5, py: 1, alignItems: 'center' }}>
          <Avatar
            sx={{
              width: 44,
              height: 44,
              fontSize: 16,
              color: '#fff',
              background: 'linear-gradient(135deg, #0F8A73, #05473E)',
            }}
          >
            {initials(me.name)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap>
              {me.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {me.email}
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }}>
              <Chip size="small" label={role} color="primary" variant="outlined" />
              <Chip
                size="small"
                label={me.mfaEnabled ? 'MFA on' : 'MFA off'}
                color={me.mfaEnabled ? 'success' : 'warning'}
                variant="outlined"
              />
            </Stack>
          </Box>
        </Stack>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => go('/account')}>
          <ListItemIcon>
            <PersonOutlineRounded fontSize="small" />
          </ListItemIcon>
          Account &amp; security
        </MenuItem>
        {!me.mfaEnabled && (
          <MenuItem onClick={() => go('/account/mfa')}>
            <ListItemIcon>
              <GppMaybeRounded fontSize="small" />
            </ListItemIcon>
            Set up two-step verification
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setAnchor(null);
            void logout().then(() => navigate('/login'));
          }}
        >
          <ListItemIcon>
            <LogoutRounded fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
}
